import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { FlightTicketPropType } from '../../../../PropTypes';
import { Form, Col, Row } from 'react-bootstrap';
import Datetime from 'react-datetime';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { toastr } from 'Helpers/ToastHelper';
import moment from 'moment';
import { DATE_FORMAT_WEB, DATE_FORMAT_ISO } from 'Constants/DateFormatConstants';
import { DateTimeFormater } from 'Helpers/DateTimeHelper';
import { processEmailsForDisplay, TUI_EMAIL_REGEX } from 'Helpers/FlightTicketHelper';

const FlightInfoSection = ({
  flyToWorkTicketInfo,
  mappedAirports,
  handleUpdateFlightTicketInfo,
  isPreferredFlightDateDisabled,
  isPreferredFlightDateValid,
  isArrivalDepartureAirportDisabled,
  isOSFieldDisabled,
  darkMode,
  getReactSelectStyles,
}) => {
  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0 });
  const datetimeRef = useRef();

  const attachCalendarLeaveListener = () => {
    const calendar = document.querySelector('.rdtPicker');
    if (calendar) {
      calendar.removeEventListener('mouseleave', handleDayMouseLeave);
      calendar.addEventListener('mouseleave', handleDayMouseLeave);
    }
  };

  const handleCalendarOpen = () => {
    setTimeout(() => {
      attachDayListeners();
      attachNavListeners();
      attachCalendarLeaveListener();
    }, 100);
  };

  const attachDayListeners = () => {
    const days = document.querySelectorAll('.rdtPicker td.rdtDay');
    days.forEach((day) => {
      day.removeEventListener('mousemove', handleDayMouseMove);
      day.removeEventListener('mouseleave', handleDayMouseLeave);
      day.addEventListener('mousemove', handleDayMouseMove);
      day.addEventListener('mouseleave', handleDayMouseLeave);
    });
  };

  const attachNavListeners = () => {
    const navButtons = document.querySelectorAll('.rdtPrev, .rdtNext, .rdtSwitch');
    navButtons.forEach((btn) => {
      btn.addEventListener('click', () => {
        setTimeout(() => {
          attachDayListeners();
          attachNavListeners();
        }, 100);
      });
    });
  };

  const handleDayMouseMove = (e) => {
    const cursor = window.getComputedStyle(e.target).cursor;
    if (cursor === 'not-allowed') {
      setTooltip({ visible: true, x: e.clientX + 10, y: e.clientY + 10 });
    } else {
      setTooltip({ visible: false, x: 0, y: 0 });
    }
  };

  const handleDayMouseLeave = () => {
    setTooltip({ visible: false, x: 0, y: 0 });
  };

  const emailValue = processEmailsForDisplay(flyToWorkTicketInfo?.additionNotificationEmails);

  return (
    <>
      <Row>
        <Form.Group as={Col}>
          <Form.Label>
            Planned Assignment {flyToWorkTicketInfo?.direction?.toLowerCase() === 'arriving' ? 'Start' : 'End'} Date
          </Form.Label>
          <Datetime
            timeFormat={false}
            dateFormat={DATE_FORMAT_WEB}
            closeOnSelect
            utc={true}
            inputProps={{ placeholder: DATE_FORMAT_WEB, disabled: true }}
            value={DateTimeFormater(flyToWorkTicketInfo.plannedAssignmentStartDate, null, DATE_FORMAT_WEB)}
            onChange={(e) => handleUpdateFlightTicketInfo('plannedAssignmentStartDate', e.format(DATE_FORMAT_ISO))}
          />
        </Form.Group>
        {tooltip.visible && (
          <div
            style={{
              position: 'fixed',
              top: tooltip.y,
              left: tooltip.x,
              background: '#222',
              color: '#fff',
              padding: '6px 12px',
              borderRadius: 4,
              fontSize: 13,
              pointerEvents: 'none',
              zIndex: 99999999,
            }}
          >
            You are trying to request flight for date outside of the allow window. Please double check the date and if
            necessary contact Workforce Planning Team to amend the Planned{' '}
            {flyToWorkTicketInfo.direction?.toLowerCase() === 'arriving' ? 'Start' : 'End'} Date
          </div>
        )}
        <Form.Group as={Col}>
          <Form.Label>Preferred Flight Date</Form.Label>
          <Datetime
            ref={datetimeRef}
            timeFormat={false}
            dateFormat={DATE_FORMAT_WEB}
            closeOnSelect
            utc={true}
            inputProps={{ placeholder: DATE_FORMAT_WEB, disabled: isPreferredFlightDateDisabled() }}
            value={
              flyToWorkTicketInfo.preferredFlightDate
                ? DateTimeFormater(flyToWorkTicketInfo.preferredFlightDate, null, DATE_FORMAT_WEB)
                : ''
            }
            isValidDate={isPreferredFlightDateValid}
            onChange={(e) => {
              if (!e) return null;
              if (!isPreferredFlightDateValid(moment.utc(e))) return;
              handleUpdateFlightTicketInfo('preferredFlightDate', e.format(DATE_FORMAT_ISO));
            }}
            onOpen={handleCalendarOpen}
          />
        </Form.Group>
        <Form.Group as={Col}>
          <Form.Label>Type of flight</Form.Label>
          <Form.Control type="text" defaultValue={flyToWorkTicketInfo.typeOfFlight || ''} disabled />
        </Form.Group>
      </Row>
      <Row>
        <Form.Group as={Col}>
          <Form.Label>Additional email to notification</Form.Label>
          <CreatableSelect
            isSearchable
            isClearable
            isMulti
            onChange={(e) => {
              if (!e) {
                handleUpdateFlightTicketInfo('additionNotificationEmails', []);
                return;
              }
              const emailRegex = TUI_EMAIL_REGEX;
              if (!e.every((email) => emailRegex.test(email.value))) {
                const invalidEmail = e[e.length - 1];
                toastr.warning('Error', `${invalidEmail.value} is invalid email, please review.`);
                return;
              }
              handleUpdateFlightTicketInfo(
                'additionNotificationEmails',
                e.map((x) => x.value)
              );
            }}
            value={emailValue}
            isDisabled={isOSFieldDisabled()}
            styles={getReactSelectStyles(darkMode)}
          />
        </Form.Group>
        <Form.Group as={Col}>
          <Form.Label>Departure Airports</Form.Label>
          <Select
            options={mappedAirports}
            isSearchable
            isClearable
            isMulti
            onChange={(e) => {
              handleUpdateFlightTicketInfo(
                'departureAirports',
                e.map((x) => x.value)
              );
            }}
            value={mappedAirports
              .filter((x) => flyToWorkTicketInfo.departureAirports?.includes(x.value) || null)
              .sort(
                (a, b) =>
                  flyToWorkTicketInfo.departureAirports.indexOf(a.value) -
                  flyToWorkTicketInfo.departureAirports.indexOf(b.value)
              )}
            isDisabled={isArrivalDepartureAirportDisabled()}
            styles={getReactSelectStyles(darkMode)}
          />
        </Form.Group>
        <Form.Group as={Col}>
          <Form.Label>Arrival Airports</Form.Label>
          <Select
            options={mappedAirports}
            isSearchable
            isClearable
            isMulti
            onChange={(e) => {
              handleUpdateFlightTicketInfo(
                'arrivalAirports',
                e.map((x) => x.value)
              );
            }}
            value={mappedAirports
              .filter((x) => flyToWorkTicketInfo.arrivalAirports?.includes(x.value) || null)
              .sort(
                (a, b) =>
                  flyToWorkTicketInfo.arrivalAirports.indexOf(a.value) -
                  flyToWorkTicketInfo.arrivalAirports.indexOf(b.value)
              )}
            isDisabled={isArrivalDepartureAirportDisabled()}
            styles={getReactSelectStyles(darkMode)}
          />
        </Form.Group>
      </Row>
    </>
  );
};

FlightInfoSection.propTypes = {
  flyToWorkTicketInfo: FlightTicketPropType.isRequired,
  mappedAirports: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.string, label: PropTypes.string })).isRequired,
  handleUpdateFlightTicketInfo: PropTypes.func.isRequired,
  isPreferredFlightDateDisabled: PropTypes.func.isRequired,
  isPreferredFlightDateValid: PropTypes.func.isRequired,
  isArrivalDepartureAirportDisabled: PropTypes.func.isRequired,
  isOSFieldDisabled: PropTypes.func.isRequired,
  darkMode: PropTypes.bool.isRequired,
  getReactSelectStyles: PropTypes.func.isRequired,
};

export default React.memo(FlightInfoSection);
